import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePage } from '@inertiajs/react';
import { cn } from '../../lib/cn';

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'zh-CN', label: '中' },
];

const normalize = (lng) => (lng?.startsWith('zh') ? 'zh-CN' : 'en');

export default function LanguageToggle() {
  const { t, i18n } = useTranslation();
  const { auth } = usePage().props;
  const [current, setCurrent] = useState(() => normalize(i18n.language));

  useEffect(() => {
    const onChange = (lng) => setCurrent(normalize(lng));
    i18n.on('languageChanged', onChange);
    setCurrent(normalize(i18n.language));
    return () => i18n.off('languageChanged', onChange);
  }, [i18n]);

  const switchLocale = async (code) => {
    if (code === current) return;
    await i18n.changeLanguage(code);
    localStorage.setItem('locale', code);

    if (auth?.user) {
      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
        await fetch('/locale', {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({ locale: code }),
        });
      } catch (_) {
        // Already changed client-side; server sync is best-effort.
      }
    }
  };

  return (
    <div className="flex items-center justify-between px-4 py-2">
      <span className="text-sm text-gray-700 dark:text-gray-300">
        {t('language_toggle.label')}
      </span>
      <div className="flex items-center gap-1 rounded-md bg-gray-100 dark:bg-gray-700 p-0.5">
        {LANGUAGES.map(({ code, label }) => {
          const isActive = current === code;
          return (
            <button
              key={code}
              type="button"
              onClick={() => switchLocale(code)}
              className={cn(
                'px-2 py-0.5 text-xs font-medium rounded transition-colors',
                isActive
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              )}
              aria-pressed={isActive}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
